import {
  Badge,
  Button,
  Menu,
  MenuButton,
  MenuButtonProps,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  SplitButton,
  Tooltip,
} from '@fluentui/react-components';
import { ArrowDownloadRegular, CalendarEditRegular, MoreHorizontalRegular } from '@fluentui/react-icons';
import React, { useState } from 'react';
import { showLoadingToastNotification } from '../../../../@data';
import { PubCenterReportingDataResponse } from '../../DashboardCards/@data/store/schema';
import { ChartGrain } from '../../DashboardCards/ReportCard/ReportCard.types';
import { exportToPNG } from '../../utils/exportHelper';
import { useReportDetailPageStyles } from '../DetailPage.styles';
import { downloadReport } from '../utils/utils';
import { RenameReportDialog } from './RenameReportDialog';
import { SchedulePanel } from './SchedulePanel';

interface IReportActionProps {
  reportData: PubCenterReportingDataResponse | undefined;
  downloadReportName: string;
  cardRef: React.RefObject<HTMLDivElement>;
  isReportDataLoaded?: boolean;
  isReportUnsaved?: boolean;
  isReportPremade?: boolean;
  isNewReport: boolean;
  isRangeTypeCustom: boolean;
  reportName: string;
  isReportScheduled: boolean;
  defaultScheduleFreq?: ChartGrain;
  defaultScheduleEmails?: string[];
  handleOnRename: (newName: string) => void;
  onSave: () => void;
  onSaveAsCopy: () => void;
  onDelete: () => void;
  onSchedule: (enabled: boolean, emails?: string[], frequency?: ChartGrain) => void;
}

export const ReportActionComponent: React.FC<IReportActionProps> = ({
  reportData,
  cardRef,
  isReportDataLoaded,
  isReportUnsaved,
  downloadReportName,
  isReportPremade,
  isNewReport,
  isRangeTypeCustom,
  reportName,
  isReportScheduled,
  defaultScheduleFreq,
  defaultScheduleEmails,
  handleOnRename,
  onSave,
  onSaveAsCopy,
  onDelete,
  onSchedule,
}) => {
  const classNames = useReportDetailPageStyles();

  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isSchedulePanelOpen, setIsSchedulePanelOpen] = useState(false);

  const handleRenameSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleOnRename(event.currentTarget['report-name-input'].value);
    setIsRenameDialogOpen(false);
  };

  return (
    <div className={classNames.reportActionContainerStyle}>
      <div className={classNames.reportTitleContainerStyle}>
        {isReportUnsaved && (
          <Badge appearance="filled" color="informative" shape="circular">
            Unsaved
          </Badge>
        )}
        <h2 className={classNames.reportTitleStyle}>{reportName}</h2>
        {isReportUnsaved && (
          <RenameReportDialog
            isOpen={isRenameDialogOpen}
            setIsOpen={setIsRenameDialogOpen}
            handleRenameSubmit={handleRenameSubmit}
            reportName={reportName}
          />
        )}
      </div>
      <div className={classNames.reportActionStyle}>
        <span>Currency: USD</span>

        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <Tooltip content={{ children: 'Download' }} relationship="label" appearance="normal">
              <MenuButton icon={<ArrowDownloadRegular />} className={classNames.dropdownStyle} aria-label="Download">
                {' '}
              </MenuButton>
            </Tooltip>
          </MenuTrigger>

          <MenuPopover>
            <MenuList>
              <MenuItem onClick={() => downloadReport(reportData?.DownloadUrl, downloadReportName, true)} disabled={!isReportDataLoaded}>
                Download Excel csv
              </MenuItem>
              <MenuItem
                disabled={!isReportDataLoaded}
                onClick={async () => {
                  showLoadingToastNotification({
                    text: 'Export To PNG',
                    onDismiss: async () => {
                      cardRef.current && (await exportToPNG(cardRef.current, downloadReportName));
                    },
                  });
                }}
              >
                Download graph png
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>

        <SchedulePanel
          key={`${isReportScheduled}-${defaultScheduleFreq}-${defaultScheduleEmails?.join(',')}`}
          isOpen={isSchedulePanelOpen}
          setIsOpen={setIsSchedulePanelOpen}
          onSave={onSchedule}
          defaultEnabled={isReportScheduled}
          defaultEmails={defaultScheduleEmails}
          defaultFreq={defaultScheduleFreq}
        />
        <Tooltip
          content={{
            children: isReportUnsaved
              ? 'Save the report to schedule'
              : isRangeTypeCustom
              ? 'custom date range report scheduling is not supported'
              : 'Schedule',
          }}
          relationship="label"
          appearance="normal"
        >
          <Button
            icon={<CalendarEditRegular />}
            aria-label="Schedule report"
            disabled={isReportUnsaved}
            onClick={() => setIsSchedulePanelOpen(true)}
          />
        </Tooltip>

        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <MenuButton icon={<MoreHorizontalRegular />} aria-label="More options" />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem disabled={isReportPremade || isNewReport} onClick={onDelete}>
                Delete report
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>

        <div className={classNames.separatorLineStyle} />

        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            {(triggerProps: MenuButtonProps) => (
              <SplitButton
                className={classNames.dropdownStyle}
                menuButton={triggerProps}
                primaryActionButton={{
                  onClick: onSave,
                }}
                appearance="primary"
                disabled={!isReportUnsaved}
              >
                Save
              </SplitButton>
            )}
          </MenuTrigger>

          <MenuPopover>
            <MenuList>
              <MenuItem disabled={isReportPremade || isNewReport} onClick={onSaveAsCopy}>
                Save as copy
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </div>
  );
};
